import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_IMAGE,
  SITE_NAME,
  SITE_URL,
  getCanonicalPath,
  getSeoForPath,
} from "./seoConfig";

const setMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const setLinkTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
};

const setJsonLd = (id, data) => {
  let tag = document.getElementById(id);

  if (!data) {
    tag?.remove();
    return;
  }

  if (!tag) {
    tag = document.createElement("script");
    tag.id = id;
    tag.type = "application/ld+json";
    document.head.appendChild(tag);
  }

  tag.textContent = JSON.stringify(data);
};

const buildBreadcrumbSchema = (canonicalPath, title) => {
  if (canonicalPath === "/") return null;

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Home",
        item: SITE_URL,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: title.replace(` | ${SITE_NAME}`, ""),
        item: `${SITE_URL}${canonicalPath}`,
      },
    ],
  };
};

const SEOManager = () => {
  const location = useLocation();

  useEffect(() => {
    const canonicalPath = getCanonicalPath(location.pathname);
    const seo = getSeoForPath(canonicalPath);
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const imageUrl = `${SITE_URL}${seo.image || DEFAULT_IMAGE}`;

    document.title = seo.title;

    setMetaTag('meta[name="description"]', {
      name: "description",
      content: seo.description,
    });
    setMetaTag('meta[name="robots"]', {
      name: "robots",
      content: "index,follow,max-image-preview:large",
    });
    setMetaTag('meta[property="og:site_name"]', {
      property: "og:site_name",
      content: SITE_NAME,
    });
    setMetaTag('meta[property="og:type"]', {
      property: "og:type",
      content: seo.ogType || "website",
    });
    setMetaTag('meta[property="og:title"]', {
      property: "og:title",
      content: seo.title,
    });
    setMetaTag('meta[property="og:description"]', {
      property: "og:description",
      content: seo.description,
    });
    setMetaTag('meta[property="og:url"]', {
      property: "og:url",
      content: canonicalUrl,
    });
    setMetaTag('meta[property="og:image"]', {
      property: "og:image",
      content: imageUrl,
    });
    setMetaTag('meta[name="twitter:card"]', {
      name: "twitter:card",
      content: "summary_large_image",
    });
    setMetaTag('meta[name="twitter:title"]', {
      name: "twitter:title",
      content: seo.title,
    });
    setMetaTag('meta[name="twitter:description"]', {
      name: "twitter:description",
      content: seo.description,
    });
    setMetaTag('meta[name="twitter:image"]', {
      name: "twitter:image",
      content: imageUrl,
    });
    setLinkTag('link[rel="canonical"]', {
      rel: "canonical",
      href: canonicalUrl,
    });

    setJsonLd("bhi-route-schema", seo.schema || null);
    setJsonLd(
      "bhi-breadcrumb-schema",
      buildBreadcrumbSchema(canonicalPath, seo.title)
    );
  }, [location.pathname]);

  return null;
};

export default SEOManager;
